import {
  useUploadStore,
  type ExtendedUploadItem,
} from "../../store/uploadStore";
import { useObjectRenderOverlayStore } from "../../store/objectRenderOverlayStore";

export const UNITY_OBJECT_IMPORT_STATE_EVENT = "unity:objectImportState";

/**
 * STUDIO-ASSET-003: Space 전환·unload가 in-flight VSE 로드를 취소하면 Unity는 실패 terminal에
 * 이 reasonCode를 실어 보낸다. 변환 결과는 서버에 남아 있으므로 실패가 아니라
 * `Tap to import to Space.` 대기(awaiting_user_restore)로 되돌린다.
 */
export const OBJECT_IMPORT_LOAD_CANCELLED_REASON = "object_import_load_cancelled";

export type UnityObjectImportPhase =
  "preparing" | "rendering" | "ready" | "failed";
export type UnityObjectImportStatus = "in_progress" | "success" | "failed";

export interface UnityObjectImportStatePayload {
  jobId: string | null;
  transactionId: string | null;
  runId: number | null;
  phase: UnityObjectImportPhase;
  status: UnityObjectImportStatus;
  targetSpaceIndex: number | null;
  currentSpaceIndex: number | null;
  vseIndex: number | null;
  fileName: string | null;
  reasonCode: string | null;
  message: string | null;
}

function parsePayload(input: unknown): Record<string, unknown> | null {
  if (!input) return null;
  if (typeof input === "string") {
    try {
      const parsed = JSON.parse(input);
      return parsed && typeof parsed === "object"
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return typeof input === "object" ? (input as Record<string, unknown>) : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readPhase(value: unknown): UnityObjectImportPhase | null {
  return value === "preparing" ||
    value === "rendering" ||
    value === "ready" ||
    value === "failed"
    ? value
    : null;
}

function readStatus(value: unknown): UnityObjectImportStatus | null {
  return value === "in_progress" || value === "success" || value === "failed"
    ? value
    : null;
}

export function normalizeUnityObjectImportStatePayload(
  input: unknown,
): UnityObjectImportStatePayload | null {
  const payload = parsePayload(input);
  if (!payload) return null;

  const phase = readPhase(payload.phase);
  const status = readStatus(payload.status);
  if (!phase || !status) return null;

  return {
    jobId: readString(payload.jobId),
    transactionId: readString(payload.transactionId),
    runId: readNumber(payload.runId),
    phase,
    status,
    targetSpaceIndex: readNumber(payload.targetSpaceIndex),
    currentSpaceIndex: readNumber(payload.currentSpaceIndex),
    vseIndex: readNumber(payload.vseIndex),
    fileName: readString(payload.fileName),
    reasonCode: readString(payload.reasonCode),
    message: readString(payload.message),
  };
}

const OBJECT_IMPORT_FLOWS = new Set([
  "editor_object_import",
  "editor_library_attach",
]);

function matchesObjectImportItem(
  item: ExtendedUploadItem,
  detail: UnityObjectImportStatePayload,
): boolean {
  if (
    !OBJECT_IMPORT_FLOWS.has(item.flow ?? "") ||
    item.cancelled ||
    item.paused ||
    item.status === "paused" ||
    item.status === "cancelled" ||
    item.status === "completed" ||
    item.status === "failed" ||
    item.status === "failed_capacity_post_conversion" ||
    item.editorImportRuntimeState === "failed" ||
    item.editorImportRuntimeState === "completed"
  ) {
    return false;
  }
  if (detail.jobId) {
    // An explicit attempt must never fall back to another attempt's slot.
    return item.jobId === detail.jobId || item.id === detail.jobId;
  }
  if (
    typeof detail.targetSpaceIndex === "number" &&
    item.targetSpaceIndex === detail.targetSpaceIndex
  ) {
    if (
      typeof detail.vseIndex !== "number" ||
      item.editorImportVseIndex == null ||
      item.editorImportVseIndex === detail.vseIndex
    ) {
      return true;
    }
  }
  return false;
}

export function applyUnityObjectImportStateToUploadStore(
  detail: UnityObjectImportStatePayload,
): void {
  if (
    (detail.phase === "ready" && detail.status === "success") ||
    detail.phase === "failed" ||
    detail.status === "failed"
  ) {
    useObjectRenderOverlayStore.getState().settleImport(detail);
  }

  const store = useUploadStore.getState();
  const candidates = Object.values(store.items).filter((candidate) =>
    matchesObjectImportItem(candidate, detail),
  );
  // Legacy slot-only events have no project identity. Accept only one active owner.
  if (candidates.length !== 1) return;
  const [item] = candidates;

  if (detail.phase === "rendering" || detail.phase === "preparing") {
    store.setRendering?.({
      id: item.id,
      progress: 60,
    });
    return;
  }

  if (detail.phase === "failed" || detail.status === "failed") {
    if (detail.reasonCode === OBJECT_IMPORT_LOAD_CANCELLED_REASON) {
      store.upsertItem?.({
        id: item.id,
        editorImportRuntimeState: "awaiting_user_restore",
      });
      return;
    }
    store.fail?.({
      id: item.id,
      error: detail.reasonCode ?? detail.message ?? "Object import failed.",
    });
  }
}
